// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import dayjs, { extend } from "dayjs";
// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import utc from "dayjs/plugin/utc";
import inRange from "lodash/inRange";

// Extend dayjs with UTC plugin
extend(utc);

/**
 * Converts a UTC time string (HH:mm:ss) to a local time string (HH:mm:ss)
 * @param utcTime - UTC time string in HH:mm:ss format
 * @returns Local time string in HH:mm:ss format
 */
export function utcToLocalTimeString(utcTime: string): string {
  if (!isValidTimeString(utcTime)) return "";

  const today = dayjs().format("YYYY-MM-DD");
  return dayjs.utc(`${today} ${utcTime}`).local().format("HH:mm:ss");
}

/**
 * Converts a local time string (HH:mm:ss) to a UTC time string (HH:mm:ss)
 * @param localTime - Local time string in HH:mm:ss format
 * @returns UTC time string in HH:mm:ss format
 */
export function localToUtcTimeString(localTime: string): string {
  if (!isValidTimeString(localTime)) return "";

  const today = dayjs().format("YYYY-MM-DD");
  return dayjs(`${today} ${localTime}`).utc().format("HH:mm:ss");
}

/**
 * Determines if a time string is in UTC format (HH:mm:ss)
 * @param timeString - Time string to check
 * @returns True if the string looks like a UTC time format
 */
export function isUtcTimeString(timeString: string): boolean {
  return /^\d{2}:\d{2}:\d{2}$/.test(timeString);
}

/**
 * Checks if a time string is valid in HH:mm:ss format and numeric ranges
 * @param timeString - Time string to validate
 * @returns True if the string is a valid time
 */
export function isValidTimeString(timeString: string): boolean {
  if (!timeString) return false;

  // Ensure format HH:mm:ss
  if (!/^\d{2}:\d{2}:\d{2}$/.test(timeString)) return false;

  // Validate numeric ranges
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return (
    !isNaN(hours) &&
    !isNaN(minutes) &&
    !isNaN(seconds) &&
    inRange(hours, 0, 24) &&
    inRange(minutes, 0, 60) &&
    inRange(seconds, 0, 60)
  );
}

/**
 * Normalizes a complete local time string (`HH:mm` or `HH:mm:ss`) to `HH:mm:ss`.
 * Returns null for partial or invalid input (for example while the user is still typing).
 */
export function normalizeLocalTimeStringToHHmmss(timeString: string): string | null {
  const trimmed = timeString.trim();
  if (!trimmed) {
    return null;
  }

  if (isValidTimeString(trimmed)) {
    return trimmed;
  }

  const hhMmMatch = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
  if (!hhMmMatch) {
    return null;
  }

  const hours = Number(hhMmMatch[1]);
  const minutes = Number(hhMmMatch[2]);
  if (isNaN(hours) || isNaN(minutes) || !inRange(hours, 0, 24) || !inRange(minutes, 0, 60)) {
    return null;
  }

  const normalized = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  return isValidTimeString(normalized) ? normalized : null;
}

/**
 * Gets the display time value, converting UTC to local if needed
 * @param timeValue - The stored time value (could be UTC or local)
 * @returns The time value to display (always local)
 */
export function getDisplayTimeValue(timeValue: string | undefined): string {
  if (!timeValue) {
    return "";
  }

  if (isUtcTimeString(timeValue)) {
    // If value looks like a UTC time string, convert to local for display
    return utcToLocalTimeString(timeValue);
  }

  // Already a local time string
  return timeValue;
}

/**
 * Gets the current time as a local time string (HH:mm:ss)
 * @returns Current local time in HH:mm:ss format
 */
export function getCurrentLocalTimeString(): string {
  return dayjs().format("HH:mm:ss");
}

/**
 * Converts a local datetime string to a UTC datetime string
 * @param localDateTime - Local datetime string in ISO format or any valid datetime format
 * @returns UTC datetime string in ISO format
 */
export function localToUtcDateTimeString(localDateTime: string): string {
  if (!localDateTime) {
    return "";
  }

  try {
    const localDate = dayjs(localDateTime);
    if (!localDate.isValid()) {
      return "";
    }
    return localDate.utc().format();
  } catch (error) {
    return "";
  }
}

/**
 * Converts a UTC datetime string to a local datetime string
 * @param utcDateTime - UTC datetime string in ISO format
 * @returns Local datetime string in ISO format
 */
export function utcToLocalDateTimeString(utcDateTime: string): string {
  if (!utcDateTime) {
    return "";
  }

  try {
    const utcDate = dayjs.utc(utcDateTime);
    if (!utcDate.isValid()) {
      return "";
    }
    return utcDate.local().format();
  } catch (error) {
    return "";
  }
}

/**
 * Formats a datetime string to "Initiated 10:49:20 AM · September 12, 2025" format
 * @param dateTimeString - ISO datetime string
 * @returns Formatted string or undefined if invalid
 */
export function formatInitiatedTimestamp(dateTimeString: string | undefined): string | undefined {
  if (!dateTimeString) return undefined;

  const date = dayjs(dateTimeString);
  if (!date.isValid()) return undefined;

  const timeStr = date.local().format("h:mm:ss A");
  const dateStr = date.local().format("MMMM D, YYYY");

  return `Initiated ${timeStr} · ${dateStr}`;
}

export default {
  utcToLocalTimeString,
  localToUtcTimeString,
  isUtcTimeString,
  isValidTimeString,
  normalizeLocalTimeStringToHHmmss,
  getDisplayTimeValue,
  getCurrentLocalTimeString,
  localToUtcDateTimeString,
  utcToLocalDateTimeString,
  formatInitiatedTimestamp,
};
